import { useEffect, useRef } from "react";
import { gsap, prefersReducedMotion } from "../lib/motion";
import logoLight from "../assets/logo/logo-light.webp";
import "./Preloader.css";

/**
 * Wall-clock ceiling for the curtain.
 *
 * The reveal is driven by GSAP, i.e. by requestAnimationFrame — and rAF is
 * throttled to nothing in a background tab and starved behind long tasks on a
 * slow device. So the timeline's `onComplete` can never be the only thing that
 * unlocks the page: without this ceiling a visitor who opens the site in a
 * background tab and comes back later is left staring at a black rectangle.
 * The intro itself runs ~1.3s, so this is pure headroom, never the happy path.
 */
export const PRELOADER_MAX_MS = 2200;

/**
 * Brand curtain. Counts to 100 while fonts/images settle, then wipes upward
 * and hands off to the hero timeline via `onDone`.
 */
export default function Preloader({ onDone }) {
  const rootRef = useRef(null);
  const barRef = useRef(null);
  const pctRef = useRef(null);
  const logoRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    let tl = null;
    let settled = false;
    let failsafe = 0;

    /**
     * The single exit door. Every path — normal completion, hidden tab, wall
     * clock, thrown error — goes through here, so the body is always unlocked
     * and `onDone` fires exactly once.
     *
     * `bail` means rAF is not to be trusted, so the curtain is dismissed with
     * a class (compositor-driven CSS) instead of a tween.
     */
    const finish = (bail) => {
      if (settled) return;
      settled = true;

      window.clearTimeout(failsafe);
      document.removeEventListener("visibilitychange", onVisibility);
      document.body.classList.remove("is-locked");

      if (bail) {
        tl?.kill();
        root?.classList.add("preloader--bail");
      }

      onDone?.();
    };

    /** A hidden document gets no frames, so there is nothing left to wait for. */
    const onVisibility = () => {
      if (document.visibilityState === "hidden") finish(true);
    };

    if (prefersReducedMotion()) {
      gsap.set(root, { display: "none" });
      finish(false);
      return undefined;
    }

    // Loaded straight into a background tab: reveal now, don't animate to nobody.
    if (document.visibilityState === "hidden") {
      finish(true);
      return undefined;
    }

    document.addEventListener("visibilitychange", onVisibility);
    failsafe = window.setTimeout(() => finish(true), PRELOADER_MAX_MS);

    document.body.classList.add("is-locked");
    const progress = { n: 0 };

    try {
      tl = gsap.timeline({ onComplete: () => finish(false) });

      tl.fromTo(
        logoRef.current,
        { autoAlpha: 0, y: 18, filter: "blur(8px)" },
        { autoAlpha: 1, y: 0, filter: "blur(0px)", duration: 0.42, ease: "power3.out" }
      )
        .to(
          progress,
          {
            n: 100,
            duration: 0.55,
            ease: "power2.inOut",
            onUpdate: () => {
              const v = Math.round(progress.n);
              if (pctRef.current) pctRef.current.textContent = String(v).padStart(3, "0");
            },
          },
          0.06
        )
        .fromTo(barRef.current, { scaleX: 0 }, { scaleX: 1, duration: 0.55, ease: "power2.inOut" }, 0.06)
        .to([logoRef.current, ".preloader__meter"], {
          autoAlpha: 0,
          y: -14,
          duration: 0.24,
          ease: "power2.in",
        })
        .to(
          root,
          {
            clipPath: "inset(0% 0% 100% 0%)",
            duration: 0.55,
            ease: "expo.inOut",
          },
          "-=0.08"
        )
        .set(root, { display: "none" });
    } catch {
      // A broken intro must never cost the visitor the page.
      finish(true);
    }

    return () => {
      window.clearTimeout(failsafe);
      document.removeEventListener("visibilitychange", onVisibility);
      tl?.kill();
      document.body.classList.remove("is-locked");
    };
  }, [onDone]);

  return (
    <div className="preloader" ref={rootRef} role="status" aria-live="polite">
      <span className="sr-only">Loading Private Stays</span>
      <div className="preloader__inner">
        <img
          ref={logoRef}
          className="preloader__logo"
          src={logoLight}
          alt=""
          width="601"
          height="165"
        />
        <div className="preloader__meter">
          <div className="preloader__track">
            <span className="preloader__bar" ref={barRef} />
          </div>
          <span className="preloader__pct" ref={pctRef}>
            000
          </span>
        </div>
      </div>
    </div>
  );
}
